import React, { useState, useEffect } from 'react';
import { serverGroupAPI } from '../services/api';

const PROVIDER_COLORS = { digitalocean: '#0080ff', vultr: '#007bfc', aws: '#ff9900', gcp: '#4285f4', azure: '#0078d4' };
const token = () => localStorage.getItem('mcp_token');
const fetchServers = async () => {
  const r = await fetch('/api/servers', { headers: { Authorization: `Bearer ${token()}` } });
  return r.json();
};

function ServerCard({ member }) {
  const s = member.serverId;
  if (!s) return null;
  return (
    <div style={{
      flex: 1, minWidth: 220, border: '1px solid var(--border)', borderRadius: 10,
      padding: 14, background: 'var(--bg-secondary)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: 13, color: 'var(--text-primary)' }}>{s.name}</strong>
        {member.role && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
            background: 'var(--accent-glow)', color: 'var(--accent)', textTransform: 'uppercase',
          }}>{member.role}</span>
        )}
      </div>
      <div style={{ fontSize: 11, color: PROVIDER_COLORS[s.provider] || 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>
        {s.provider}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', marginBottom: 10 }}>
        {s.ipAddress || 'No IP'}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
        ${(s.monthlyCost || 0).toFixed(2)}/mo
      </div>
      <a href={`/servers?ssh=${s._id}`} style={{
        display: 'inline-block', fontSize: 11, padding: '5px 10px', borderRadius: 6,
        border: '1px solid var(--border)', color: 'var(--text-secondary)', textDecoration: 'none',
      }}>🖥️ SSH / Monitor →</a>
    </div>
  );
}

export default function ServerGroups() {
  const [groups, setGroups] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: '', memberA: '', roleA: '', memberB: '', roleB: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [g, s] = await Promise.all([serverGroupAPI.list(), fetchServers()]);
      setGroups(g.data.data || []);
      setServers(s.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.memberA || !form.memberB) return;
    setSaving(true);
    try {
      await serverGroupAPI.create({
        name: form.name,
        members: [
          { serverId: form.memberA, role: form.roleA || 'Server A' },
          { serverId: form.memberB, role: form.roleB || 'Server B' },
        ],
      });
      setModal(false);
      setForm({ name: '', memberA: '', roleA: '', memberB: '', roleB: '' });
      load();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await serverGroupAPI.remove(id);
    load();
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Server Groups</h1>
          <p>Combine servers across different cloud providers into one logical unit</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ Create Group</button>
      </div>

      {groups.length === 0 ? (
        <div className="card empty">
          <div className="empty-icon">🔗</div>
          <h3>No server groups yet</h3>
          <p>Combine servers from different providers — e.g. one for storage, one for compute</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModal(true)}>Create Your First Group</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {groups.map(g => {
            const totalCost = (g.members || []).reduce((sum, m) => sum + (m.serverId?.monthlyCost || 0), 0);
            return (
              <div className="card" key={g._id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <h2 style={{ fontSize: 16 }}>{g.name}</h2>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Combined cost: ${totalCost.toFixed(2)}/mo</span>
                  </div>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleDelete(g._id)}>Remove Group</button>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {(g.members || []).map((m, i) => <ServerCard member={m} key={i} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal">
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2>Create Server Group</h2>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>Group Name</label>
              <input className="form-control" placeholder="e.g. My Hybrid Stack"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>Server 1</label>
              <select className="form-control" value={form.memberA} onChange={e => setForm({ ...form, memberA: e.target.value })}>
                <option value="">Select a server...</option>
                {servers.map(s => <option key={s._id} value={s._id}>{s.name} — {s.provider} ({s.ipAddress})</option>)}
              </select>
              <input className="form-control" style={{ marginTop: 8 }} placeholder="Role (e.g. Storage)"
                value={form.roleA} onChange={e => setForm({ ...form, roleA: e.target.value })} />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Server 2</label>
              <select className="form-control" value={form.memberB} onChange={e => setForm({ ...form, memberB: e.target.value })}>
                <option value="">Select a server...</option>
                {servers.map(s => <option key={s._id} value={s._id}>{s.name} — {s.provider} ({s.ipAddress})</option>)}
              </select>
              <input className="form-control" style={{ marginTop: 8 }} placeholder="Role (e.g. RAM / Compute)"
                value={form.roleB} onChange={e => setForm({ ...form, roleB: e.target.value })} />
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} disabled={saving} onClick={handleCreate}>
              {saving ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
